export const VALID_THEMES = ['light', 'dark', 'custom']

// Themes whose palette is dark. 'custom' counts: its --layout is #17181e.
const DARK_THEMES = ['dark', 'custom']

// Must match --layout per theme in src/global.css.
export const LAYOUT_COLOR = {
    light: '#f8f7fa',
    dark: '#09090b',
    custom: '#17181e',
}

/**
 * Read the persisted theme straight out of localStorage, bypassing redux.
 *
 * This is the same read the pre-paint script in public/index.html does, and
 * it exists for the two moments when the redux store is not a usable source
 * of truth:
 *
 * - Before redux-persist has rehydrated, when state.sideBar.theme is still
 *   null (Layout's restoration path).
 * - When a caller simply did not have the theme to hand. That is not
 *   hypothetical: the My Incidents table rendered its links without the
 *   theme prop, and the old `|| LAYOUT_COLOR.light` fallback below turned
 *   that omission into a bright placeholder in dark mode - the flash this
 *   whole mechanism exists to prevent. Falling back to storage instead of
 *   to light means a missing theme degrades to "correct" rather than "wrong".
 *
 * Returns null when nothing valid is stored.
 */
export const readPersistedTheme = () => {
    try {
        const persisted = localStorage.getItem('persist:sideBar')
        if (!persisted) return null
        const theme = JSON.parse(JSON.parse(persisted).theme)
        return VALID_THEMES.includes(theme) ? theme : null
    } catch (error) {
        console.warn('Failed to read persisted theme:', error)
        return null
    }
}

/**
 * Apply a theme to the document.
 *
 * Sets three things on <html>:
 *
 * - An inline background-color, so the canvas is correct even in windows
 *   where global.css hasn't applied yet - most notably the webpack dev
 *   server (react-scripts start), where our CSS is bundled inside the JS via
 *   style-loader instead of served as a <link>, so it only takes effect once
 *   the whole bundle has downloaded and executed. Until then, an inline
 *   style is the only thing that reliably out-cascades bootstrap.min.css's
 *   plain (non-important) `body { background-color: #fff }`.
 * - `color-scheme`, the same idea at the OS/UA level (native scrollbars,
 *   form controls, etc.).
 * - `data-theme`, which selects the real palette in global.css once that
 *   stylesheet is active.
 *
 * The pre-paint copy of this logic lives inline in public/index.html - it
 * has to run before the JS bundle loads, so it cannot import from here.
 * Keep the theme list and LAYOUT_COLOR in sync with that copy.
 */
export const applyTheme = (theme) => {
    if (!VALID_THEMES.includes(theme)) return

    const root = document.documentElement
    root.setAttribute('data-theme', theme)
    root.style.backgroundColor = LAYOUT_COLOR[theme]
    root.style.colorScheme = DARK_THEMES.includes(theme) ? 'dark' : 'light'

    // global.css also has rules keyed on [data-theme] on the body.
    document.getElementById('main-body')?.setAttribute('data-theme', theme)
}

/**
 * Open `url` in a new tab that is the correct theme color from its very
 * first frame - before any network request for `url` has even started.
 *
 * Why this exists: plain window.open(url, '_blank') creates an empty tab and
 * then begins fetching `url`. Until that fetch's response arrives and
 * public/index.html's pre-paint script can run, the tab shows the browser's
 * plain white default. That gap cannot be closed from inside the destination
 * page, because none of its code - however early it's placed - can run
 * before its own bytes exist. (Confirmed directly: the pre-paint script
 * reliably applies the theme within a few hundred ms of its own document
 * starting to load, but nothing can make that document start loading any
 * sooner.)
 *
 * The fix moves the work to before that request even begins: open a blank
 * tab (synchronous, no network), write a themed placeholder into it via the
 * DOM (also synchronous, still no network), then navigate that same tab to
 * `url`. Because this is now a normal in-place navigation of an
 * already-rendered document rather than a fresh tab's first load, the
 * browser keeps the placeholder on screen until the real page is ready to
 * replace it - the same way any page-to-page navigation never flashes blank
 * mid-transition.
 *
 * The catch, and the reason an earlier version of this still flashed: the
 * placeholder only helps if it is actually PAINTED first. Calling
 * location.replace() synchronously right after document.write() does not
 * give the renderer a chance to composite anything, so the navigation
 * usually commits over a document that never made it to the screen and the
 * tab stays on the browser's white surface for the whole load after all.
 * That is a race, which is exactly why the bug looked fixed on some runs and
 * not others - measured on this build against a 1.5s stylesheet, the
 * placeholder won only 1 time in 6. So the navigation is deferred until the
 * placeholder has provably been painted: two nested animation frames (the
 * second fires once the first frame is committed), with a setTimeout as a
 * backstop for the case where rAF is throttled and never fires, e.g. if the
 * tab is opened in the background. The cost is one or two frames of extra
 * latency before the real request starts; the alternative is up to a second
 * and a half of white.
 *
 * This needs a live reference to the new window, so it cannot pass
 * noopener/noreferrer - both make window.open() return null; that is
 * specifically what they are for. That trade-off is fine here because `url`
 * is always our own same-origin app, never third-party content, so the
 * reverse-tabnabbing risk those flags guard against does not apply.
 */
export const openThemedTab = (url, theme) => {
    const newTab = window.open('', '_blank')
    if (!newTab) {
        // Popup blocked - most likely this wasn't called synchronously from
        // a direct user gesture. Fall back rather than silently do nothing.
        window.open(url, '_blank', 'noopener,noreferrer')
        return
    }

    // Never fall straight back to light: an unknown/missing theme means the
    // caller didn't know, not that the user is in light mode. Ask storage
    // (the same source the pre-paint script uses) before giving up.
    const resolved = VALID_THEMES.includes(theme) ? theme : readPersistedTheme()
    const color = LAYOUT_COLOR[resolved] || LAYOUT_COLOR.light
    const scheme = DARK_THEMES.includes(resolved) ? 'dark' : 'light'
    // Escape the URL for embedding in an inline <script>: JSON.stringify
    // handles quoting, and neutralising '<' stops any '</script>' inside it
    // from ending the element early.
    const urlLiteral = JSON.stringify(String(url)).replace(/</g, '\\u003c')

    // replace(), not setting .href: the placeholder isn't a real page the
    // user should ever land back on via the back button.
    const navigateOncePainted =
        '(function(){' +
        'var go=function(){if(!window.__navigated){window.__navigated=1;' +
        'location.replace(' + urlLiteral + ')}};' +
        'requestAnimationFrame(function(){requestAnimationFrame(go)});' +
        'setTimeout(go,250);' +
        '})()'

    newTab.document.write(
        '<!DOCTYPE html><html><head><meta charset="utf-8">' +
        '<meta name="color-scheme" content="' + scheme + '">' +
        '<style>html,body{background:' + color + ';margin:0;height:100%}</style>' +
        '<' + 'script>' + navigateOncePainted + '<' + '/script>' +
        '</head><body></body></html>'
    )
    newTab.document.close()
}
